import React, { useState, useEffect } from "react";
import "./Header.css";

import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import "../../i18n";

import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import type { TooltipProps } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { IconButton, Box } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import CustomHeaderButton from "./CustomHeaderButton";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import type { User } from "firebase/auth";
import { appSettings } from "../../Config/settings";

type HeaderProps = {
  user: User | null;
  onSignOut: () => void;
};

const Alert = React.forwardRef<HTMLDivElement, any>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    arrow
    placement="bottom"
    classes={{ popper: className }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.grey[900],
    color: "#fff",
    fontSize: "1rem",
    padding: "8px 14px",
    fontWeight: 500,
    borderRadius: 6,
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  },
}));

function Header({ user, onSignOut }: HeaderProps) {
  const { t } = useTranslation() as { t: (key: string) => string };

  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleContactClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!user) {
      e.preventDefault();
      setOpen(true);
    }
    setMenuOpen(false); // close menu on click
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className={`header ${scrolled ? "header-scrolled" : ""}`}>
      <div className="header_container">
        <>
          <img src={appSettings.siteLogo} alt="Logo" />
        </>

        <Box className="header_right">
          <IconButton
            className="menu_toggle"
            sx={{ display: { xs: "inline-flex", sm: "none" }, color: "white" }}
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>

          <Box className={`header_links ${menuOpen ? "open" : ""}`}>
            <LanguageSwitcher />
            <CustomHeaderButton href="#home" onClick={() => setMenuOpen(false)}>
              {t("home")}
            </CustomHeaderButton>
            <CustomTooltip title={!user ? t("pleaseLogInToContactUs") : ""}>
              <span>
                <CustomHeaderButton
                  href="#contactUs"
                  onClick={handleContactClick}
                >
                  {t("contactUs")}
                </CustomHeaderButton>
              </span>
            </CustomTooltip>
            {user && (
              <>
                <CustomHeaderButton
                  onClick={() => setConfirmLogoutOpen(true)}
                  sx={{
                    minWidth: "auto",
                    width: "auto",

                    "&:hover": {
                      backgroundColor: "#cc3e3e",
                    },
                  }}
                >
                  {t("signOut")}
                </CustomHeaderButton>

                <Dialog
                  open={confirmLogoutOpen}
                  onClose={() => setConfirmLogoutOpen(false)}
                >
                  <DialogTitle>{t("confirmSignOutTitle")}</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      {t("confirmSignOutText")}
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <CustomHeaderButton
                      onClick={() => setConfirmLogoutOpen(false)}
                      sx={{
                        color: "gray",
                      }}
                    >
                      {t("cancel")}
                    </CustomHeaderButton>
                    <CustomHeaderButton
                      onClick={() => {
                        onSignOut();
                        setConfirmLogoutOpen(true);
                      }}
                      sx={{
                        color: "#cc3e3e",
                        "&:hover": {
                          color: "#fff",
                          backgroundColor: "#cc3e3e",
                        },
                      }}
                    >
                      {t("signOut")}
                    </CustomHeaderButton>
                  </DialogActions>
                </Dialog>
              </>
            )}
          </Box>
        </Box>
      </div>

      <Snackbar open={open} autoHideDuration={4000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="info" sx={{ width: "100%" }}>
          🔒 {t("pleaseLogInToContactUs")}
        </Alert>
      </Snackbar>
    </header>
  );
}

export default Header;
